import React from "react";
import { useField, UseFieldConfig } from "react-final-form";

import { FormControl, FormHelperText } from "@mui/material";
import { styled } from "@mui/material/styles";

import { FileDropzone } from "./FileDropzone";

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.emteria.action,
    },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.emteria.error,
    },
    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        backgroundColor: theme.palette.emteria.background.inputDisabled,
    },
}));

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
    color: theme.palette.emteria.action,
    "&.Mui-error": {
        color: theme.palette.emteria.error,
    },
}));

const toFileList = (file: File): FileList => {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    return dataTransfer.files;
};

type Props = {
    name: string;
    label: React.ReactNode;
    accept?: string;
    caption?: string;
    className?: string;
    config?: UseFieldConfig;
    disabled?: boolean;
    onFileSelected?: (file: File | undefined) => void;
    showSelectedFileName?: boolean;
    style?: React.CSSProperties;
};

export const SelectFileField = ({
    name,
    label,
    accept,
    caption,
    config,
    disabled,
    onFileSelected,
    showSelectedFileName = false,
    className,
    style,
}: Props) => {
    const inputId = React.useId();
    const { input, meta } = useField<FileList | undefined>(name, config);
    const error = meta.submitFailed && meta.error;

    const handleFilesSelected = (files: File[]) => {
        const file = files[0];
        if (!file) {
            return;
        }

        input.onChange(toFileList(file));
        onFileSelected?.(file);
    };

    const selectedFileName = showSelectedFileName ? input.value?.[0]?.name : undefined;

    return (
        <StyledFormControl className={className} error={error} fullWidth style={style}>
            <FileDropzone
                inputId={inputId}
                label={label}
                accept={accept}
                caption={caption}
                disabled={disabled}
                selectedFileName={selectedFileName}
                error={error}
                onFilesSelected={handleFilesSelected}
            />
            {error ? <StyledFormHelperText error>{error}</StyledFormHelperText> : null}
        </StyledFormControl>
    );
};
